// src/pages/Cheesecake/CheesecakeCard.jsx
import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
  CircularProgress,
  Divider,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StarIcon from "@mui/icons-material/Star";
import SnackbarAlert from "../../components/SnackbarAlert";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import ProductImageGallery from "../../components/ProductImageGallery";
import { buildLineId } from "../../utils/lineId";

const CheesecakeCard = ({
  cake,
  sizes,
  isProcessing,
  setIsProcessing,
  setShowCartPreview,
  isAvailable = true,
}) => {
  const { addToCart } = useCart();
  const { user, isAuthenticated } = useAuth();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [expandIngredients, setExpandIngredients] = useState(false);

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

  // pick the single/primary cheesecake size from props
  const sizeObj =
    sizes.find((s) => s._id === cake.sizes?.[0]?._id) || sizes?.[0] || null;
  const pricePerUnit = sizeObj?.price || 0;
  const flavorName = cake.flavor || "Cheesecake";

  const allImages = [cake.images.primary, ...(cake.images.secondary || [])];
  const selectedImage = allImages[selectedIndex] || cake.images.primary;

  useEffect(() => {
    if (!cake) return;

    if (window.fbq) {
      window.fbq("track", "ViewContent", {
        content_name: cake.displayName,
        content_ids: [cake._id],
        content_type: "product",
        value: pricePerUnit,
        currency: "CAD",
      });
    }

    if (window.gtag) {
      window.gtag("event", "view_item", {
        currency: "CAD",
        value: pricePerUnit,
        items: [
          {
            item_id: cake._id,
            item_name: cake.displayName,
            item_category: cake.category || "cheesecake",
            price: pricePerUnit,
          },
        ],
      });
    }
  }, [cake, pricePerUnit]);

  const handleAddToCart = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // UI customizations (what we show in cart/checkout)
      const uiCustomizations = [
        sizeObj && { key: "size", label: "Size", value: sizeObj.displayName },
        { key: "flavor", label: "Flavor", value: flavorName },
      ].filter(Boolean);

      // Minimal, normalized snapshot for stable lineId
      const idCustomizations = uiCustomizations.map((c) => ({
        key: c.key,
        value: String(c.value).toLowerCase(),
      }));

      const type = cake.category || "cheesecake";
      const lineId = buildLineId({
        productId: cake._id,
        type,
        options: {
          sizeId: sizeObj?._id || null,
          flavorKey: flavorName.toLowerCase(),
          customizations: idCustomizations,
        },
      });

      const newItem = {
        // identity
        lineId,
        productId: cake._id,
        type,
        category: cake.category || "cheesecake",
        subcategory: null,

        // display/meta
        name: cake.name,
        displayName: cake.displayName,
        image: selectedImage,

        // options
        size: sizeObj ? { _id: sizeObj._id, name: sizeObj.displayName } : null,
        flavor: { _id: null, name: flavorName },

        // details (plural + singular for compatibility)
        customizations: uiCustomizations,
        customization: uiCustomizations,

        // pricing — listPrice is the baseline (pre-discount) per unit
        listPrice: Number(pricePerUnit),
        unitPrice: Number(pricePerUnit),
        quantity: 1,
        totalPrice: Number(pricePerUnit),
        itemsPerSet: 1,
      };

      const result = await addToCart(newItem);

      setShowCartPreview(true);
      if (result.ok && result.synced) {
        setSnackbar({
          open: true,
          message: "Item added to cart!",
          severity: "success",
        });
      } else if (result.ok && !result.synced) {
        setSnackbar({
          open: true,
          message: "Added to cart (sync failed, will retry).",
          severity: "warning",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to add item to cart. Try again.",
          severity: "error",
        });
      }

      // Analytics
      if (window.fbq) {
        window.fbq("track", "AddToCart", {
          content_name: cake.displayName,
          content_ids: [cake._id],
          content_type: "product",
          value: pricePerUnit,
          currency: "CAD",
        });
      }
      if (window.gtag) {
        window.gtag("event", "add_to_cart", {
          currency: "CAD",
          value: pricePerUnit,
          items: [
            {
              item_id: cake._id,
              item_name: cake.displayName,
              item_category: cake.category || "cheesecake",
              price: pricePerUnit,
              quantity: 1,
            },
          ],
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Card sx={{ borderRadius: 2, boxShadow: 3, p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <ProductImageGallery
            images={allImages}
            productName={cake.displayName}
            selectedIndex={selectedIndex}
            onIndexChange={setSelectedIndex}
            fullScreenOpen={fullscreenOpen}
            setFullScreenOpen={setFullscreenOpen}
            thumbnailPosition="right"
            mainImageStyles={{ height: 280, objectFit: "cover" }}
            thumbnailStyles={{ width: 60, height: 60, objectFit: "cover" }}
          />
        </Box>

        <CardContent sx={{ px: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 1,
            }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1rem", lg: "1.2rem" },
                lineHeight: 1.2,
              }}>
              {cake.displayName}
              {cake.flavor &&
                cake.flavor.toLowerCase() !==
                  cake.displayName.toLowerCase() && <> ({cake.flavor})</>}
            </Typography>

            {cake.popularity === 0 && (
              <Chip
                icon={<StarIcon sx={{ fontSize: 16 }} />}
                label="Most Popular"
                color="warning"
                size="small"
                sx={{
                  fontSize: "0.65rem",
                  height: 22,
                  "& .MuiChip-icon": { marginLeft: 0.4 },
                }}
              />
            )}
          </Box>

          {sizeObj && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Size: {sizeObj.displayName} • Servings: up to {sizeObj.servings}
            </Typography>
          )}

          <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
            ${pricePerUnit.toFixed(2)}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {cake.ingredients?.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}>
                  Ingredients
                </Typography>
                <IconButton
                  onClick={() => setExpandIngredients((prev) => !prev)}
                  size="small">
                  {expandIngredients ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={expandIngredients} timeout="auto" unmountOnExit>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: "wrap", mt: 1 }}>
                  {cake.ingredients.map((ing) => (
                    <Chip
                      key={ing._id}
                      label={ing.name}
                      size="small"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        fontSize: "0.8rem",
                        color: "#333",
                      }}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Box>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
            <Chip
              label={cake.style}
              size="small"
              sx={{
                backgroundColor: "#ccc",
                color: "#fff",
                fontWeight: 500,
                fontSize: "0.8rem",
              }}
            />
            {cake.dietaryTags?.length > 0 &&
              cake.dietaryTags.map((tag) => (
                <Chip
                  key={tag._id}
                  label={tag.label}
                  size="small"
                  sx={{
                    backgroundColor: `#${tag.color}` || "#ccc",
                    color: "#fff",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                  }}
                />
              ))}
          </Stack>

          <Box sx={{ position: "relative", mt: 3 }}>
            {isAvailable ? (
              <Button
                variant="contained"
                fullWidth
                color="primary"
                onClick={handleAddToCart}
                disabled={isProcessing}
                sx={{
                  backgroundColor: "#FD7762",
                  "&:hover": { backgroundColor: "#fd7762cc" },
                  fontWeight: "bold",
                  py: 1.25,
                  borderRadius: 2,
                }}>
                {isProcessing ? (
                  <CircularProgress color="inherit" size={22} />
                ) : (
                  "Add to Cart"
                )}
              </Button>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 1.2,
                  backgroundColor: "#f8d7da",
                  border: "1px solid #f5c2c7",
                  borderRadius: 2,
                }}>
                <Typography variant="body1" color="error" fontWeight="bold">
                  Temporarily Unavailable
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        bgColor={snackbar.severity === "warning" ? "#FF9800" : "#4CAF50"}
        iconColor="#fff"
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </>
  );
};

export default CheesecakeCard;
